import { FunctionComponent } from 'react';
import { SerializedStyles, Theme } from '@emotion/react';

interface PictureResponsiveSrc {
  xs: string;
  sm?: string;
  md?: string;
  lg?: string;
  xl?: string;
}

type PictureSrcType = string | PictureResponsiveSrc;

interface ImgProps {
  /**
   * alt text. if not present, role="presentation" will be used.
   */
  alt?: string;
  /**
   * css gets provided to img instead of root
   */
  css?: SerializedStyles | ((theme: Theme) => SerializedStyles);
  /**
   * sets an image up as decorative per (https://www.w3.org/WAI/tutorials/images/decorative/), default false
   */
  isDecorative?: boolean;
}

export interface PictureProps {
  /**
   * props for the img element.
   */
  imgProps: ImgProps;
  /**
   * src, can be a string src value or responsive sources object.
   */
  src: PictureSrcType;
  className?: string;
}

// Check if variant is a Omakase responsive mapping object
const isResponsiveObject = (src: PictureSrcType): src is PictureResponsiveSrc =>
  typeof src === 'object';

/**
 * Picture component
 */
export const Picture: FunctionComponent<PictureProps> = ({
  imgProps: { alt = '', css, isDecorative = false },
  src,
  ...rest
}) => {
  const finalAlt = isDecorative ? '' : alt;
  const role = finalAlt ? undefined : 'presentation';
  const { xs, sm, md, lg, xl }: PictureResponsiveSrc = isResponsiveObject(src) ? src : { xs: src };
  return (
    <picture {...rest}>
      {xl ? <source srcSet={xl} media="(min-width: 1920px)" /> : null}
      {lg ? <source srcSet={lg} media="(min-width: 1280px)" /> : null}
      {md ? <source srcSet={md} media="(min-width: 960px)" /> : null}
      {sm ? <source srcSet={sm} media="(min-width: 600px)" /> : null}
      <img src={xs} alt={finalAlt} role={role} css={css} />
    </picture>
  );
};

export default Picture;
